import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function ContactPage() {
  useEffect(() => { document.title = "Contact · Accommodation Suppliers"; }, []);
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [hp, setHp] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message || form.message.trim().length < 5) {
      toast.error("Please include a short message so we can help.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", { ...form, website_hp: hp });
      setSent(true);
      toast.success("Message sent — we'll reply within 1 business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="contact-success">
        <div className="w-14 h-14 rounded-full bg-[#264839] text-white flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <div className="tracking-overline mb-3 text-[#B35C4A]">Message received</div>
        <h1 className="font-serif text-3xl md:text-4xl text-[#11211A] mb-3">Thanks for reaching out</h1>
        <p className="text-[#4A5550] leading-relaxed">
          A copy of your message has been sent to our team at <span className="font-medium text-[#11211A]">hello@accommodationsuppliers.com</span>.
          You'll hear back within 1 business day.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <button onClick={() => { setSent(false); setForm({ name: "", email: "", company: "", subject: "", message: "" }); }} className="btn-secondary" data-testid="contact-send-another">Send another</button>
          <Link to="/" className="btn-primary" data-testid="contact-back-home">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="contact-page">
      <SEO title="Contact us" description="Get in touch with the Accommodation Suppliers team — buyer support, supplier onboarding, or partnership enquiries. Replies within one business day." path="/contact" />
      <div className="tracking-overline mb-3 text-[#B35C4A]">Get in touch</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#11211A] tracking-tight mb-4">Contact us</h1>
      <p className="text-[#4A5550] text-lg leading-relaxed max-w-2xl">
        Questions about a listing, a supplier, or your subscription? Send us a note and we'll reply within one business day.
      </p>

      <div className="mt-10 grid md:grid-cols-3 gap-8">
        <aside className="md:col-span-1 space-y-6 text-sm">
          <div>
            <div className="tracking-overline mb-2">Email</div>
            <a href="mailto:hello@accommodationsuppliers.com" className="text-[#264839] hover:underline flex items-center gap-2" data-testid="contact-email-link">
              <Mail className="w-4 h-4" /> hello@accommodationsuppliers.com
            </a>
          </div>
          <div>
            <div className="tracking-overline mb-2">Registered office</div>
            <div className="text-[#4A5550] leading-relaxed">
              QuArete Pty Ltd,<br />trading as Accommodation Suppliers.<br />Australia.
            </div>
          </div>
          <div>
            <div className="tracking-overline mb-2">Response time</div>
            <div className="text-[#4A5550] leading-relaxed">Within 1 business day, Mon–Fri.</div>
          </div>
        </aside>

        <form onSubmit={submit} className="md:col-span-2 space-y-4 bg-[#F0EFEA] p-6 md:p-8 border-l-4 border-[#264839]">
          {/* Honeypot: real users won't fill this — hidden field */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={hp}
            onChange={(e) => setHp(e.target.value)}
            className="hidden"
            aria-hidden="true"
            data-testid="contact-honeypot"
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Full name *</label>
              <input required value={form.name} onChange={set("name")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm bg-white" data-testid="contact-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4A5550] mb-1">Email *</label>
              <input required type="email" value={form.email} onChange={set("email")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm bg-white" data-testid="contact-email" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Company (optional)</label>
            <input value={form.company} onChange={set("company")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm bg-white" data-testid="contact-company" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Subject *</label>
            <input required value={form.subject} onChange={set("subject")} placeholder="Billing, listing help, partnership…" className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm bg-white" data-testid="contact-subject" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Message *</label>
            <textarea required minLength={5} rows={7} value={form.message} onChange={set("message")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm bg-white leading-relaxed" data-testid="contact-message" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full sm:w-auto justify-center disabled:opacity-50" data-testid="contact-submit">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send message"}
          </button>
          <p className="text-xs text-[#7D8C85] pt-1">
            By submitting this form you agree to our{" "}
            <Link to="/privacy" className="underline hover:text-[#264839]">Privacy Policy</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
